import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Loader2, Package, Sparkles,
  Layers, Server, Palette, Settings2, AlertCircle,
  Copy, Eye, EyeOff, Shield, ExternalLink, KeyRound,
} from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { slugify, validateSlug } from "./tenants/slug";

const STEPS = [
  { id: 1, key: "template", label: "Pilih Template" },
  { id: 2, key: "info", label: "Informasi Tenant" },
  { id: 3, key: "plan", label: "Paket & Langganan" },
  { id: 4, key: "review", label: "Review" },
  { id: 5, key: "create", label: "Selesai" },
];

const PACKAGES = [
  { value: "STARTER", label: "Starter", desc: "Fitur dasar toko online untuk mulai jualan." },
  { value: "GROWTH", label: "Growth", desc: "Marketing tools + laporan untuk toko berkembang." },
  { value: "ENTERPRISE", label: "Enterprise", desc: "Semua fitur + integrasi penuh." },
];

const DURATIONS = [
  { value: "MONTHLY", label: "Bulanan", desc: "Aktif 30 hari" },
  { value: "SIX_MONTHS", label: "6 Bulan", desc: "Aktif 180 hari" },
  { value: "YEARLY", label: "Tahunan", desc: "Aktif 365 hari" },
  { value: "LIFETIME", label: "Lifetime", desc: "Tanpa masa berakhir" },
];

const durationLabel = (v) => DURATIONS.find((d) => d.value === v)?.label || v;
// Teks ekspektasi berakhir — HANYA display. Tanggal pasti dihitung backend (canonical).
const expiryHint = (v) =>
  v === "LIFETIME"
    ? "tidak ada masa berakhir"
    : `Otomatis dihitung sistem: ${DURATIONS.find((d) => d.value === v)?.desc || ""} sejak aktivasi`;

const TIMEZONES = [
  "Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura",
  "Asia/Singapore", "Asia/Kuala_Lumpur", "UTC",
];

const CURRENCIES = ["IDR", "USD", "SGD", "MYR", "EUR"];

const DEFAULT_FORM = {
  template_id: "",
  tenant_name: "",
  tenant_slug: "",
  tenant_slug_edited: false,
  owner_name: "",
  owner_email: "",
  owner_phone: "",
  timezone: "Asia/Jakarta",
  currency: "IDR",
  package: "STARTER",
  subscription_type: "MONTHLY",
  notes: "",
};

// ============== Sub-components ==============
function StepIndicator({ current }) {
  return (
    <ol className="flex items-center gap-2 mb-8" data-testid="cft-progress">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <li key={s.id} className="flex items-center gap-2 flex-1 min-w-0">
            <div
              data-testid={`cft-step-indicator-${s.id}`}
              className={`w-8 h-8 shrink-0 rounded-full grid place-items-center text-xs font-black border-2 transition-all ${
                done
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : active
                  ? "border-cyan-400 text-cyan-300 bg-cyan-500/10"
                  : "border-white/20 text-white/40"
              }`}
            >
              {done ? <Check size={14} /> : s.id}
            </div>
            <span
              className={`text-[11px] font-bold uppercase tracking-widest truncate ${
                active ? "text-white" : done ? "text-emerald-300" : "text-white/40"
              }`}
            >
              {s.label}
            </span>
            {idx < STEPS.length - 1 && (
              <div className={`h-px flex-1 ${done ? "bg-emerald-500/60" : "bg-white/10"}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function TemplateCard({ tpl, selected, onSelect }) {
  const featureCount = tpl.capability_count ?? Object.values(tpl.feature_flags || {}).filter(Boolean).length;
  return (
    <button
      type="button"
      onClick={() => onSelect(tpl)}
      className={`text-left rounded-2xl border p-5 transition-all group ${
        selected
          ? "border-cyan-400 bg-gradient-to-br from-cyan-500/15 to-purple-500/10 ring-2 ring-cyan-400/40"
          : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/25"
      }`}
      data-testid={`cft-template-card-${tpl.slug}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: `linear-gradient(135deg, ${tpl.branding?.theme_color || "#06b6d4"}, ${tpl.branding?.accent_color || "#8b5cf6"})`,
          }}
        >
          <Package size={24} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-display font-black text-base truncate">{tpl.name}</h3>
            <span className="text-[10px] font-mono text-white/50">v{tpl.version}</span>
          </div>
          <div className="text-white/50 text-xs font-mono mt-0.5 truncate">{tpl.slug}</div>
        </div>
        {selected && <Check className="text-cyan-400 shrink-0" size={20} />}
      </div>
      <p className="text-white/60 text-xs line-clamp-2 mb-3 min-h-[32px]">{tpl.description || "—"}</p>
      <div className="grid grid-cols-2 gap-2 text-[11px] text-white/60">
        <div className="flex items-center gap-1.5 truncate">
          <Layers size={11} /> {tpl.category}
        </div>
        <div className="flex items-center gap-1.5 truncate">
          <Sparkles size={11} className="text-emerald-400" /> {featureCount} features
        </div>
        <div className="flex items-center gap-1.5 truncate col-span-2">
          <Server size={11} />
          <span className="truncate">
            {tpl.provider_defaults?.payment || "—"} · {tpl.provider_defaults?.shipping || "—"}
          </span>
        </div>
      </div>
    </button>
  );
}

function Field({ label, hint, error, children, testId, required }) {
  return (
    <div data-testid={testId}>
      <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">
        {label}{required && <span className="text-rose-400 ml-1">*</span>}
      </label>
      {children}
      {hint && !error && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
      {error && (
        <div className="text-[11px] text-rose-400 mt-1 flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </div>
      )}
    </div>
  );
}

function ReviewRow({ label, value, testId }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-4 py-3" data-testid={testId}>
      <div className="text-[11px] text-white/40 uppercase tracking-wider self-center">{label}</div>
      <div className="col-span-2 text-sm text-white/90 break-words">
        {value || <span className="text-white/30">—</span>}
      </div>
    </div>
  );
}

// ============== SuccessPanel (Step 4) — Tenant Admin Bootstrap credentials ==============
function SuccessPanel({ result, onOpenTenantDetail }) {
  const [showPw, setShowPw] = React.useState(false);
  const [copied, setCopied] = React.useState(null); // "email" | "password" | "both"
  const admin = result.tenant_admin;
  const tempPw = result.temporary_password;
  const tenant = result.tenant;
  const adminUrl = tenant?.tenant_slug ? `https://${tenant.tenant_slug}.dynicty.com/admin/login` : null;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label === "email" ? "Email" : label === "password" ? "Password" : "Kredensial"} disalin`);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Gagal menyalin — copy manual");
    }
  };

  return (
    <div data-testid="cft-success-panel" className="space-y-5">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-emerald-400" />
        </div>
        <div className="text-white font-display font-black text-2xl mt-3">Tenant Berhasil Dibuat!</div>
        <div className="text-white/70 text-sm mt-1">
          <span className="text-cyan-300 font-bold">{tenant.tenant_name}</span>{" · "}
          <span className="font-mono text-white/50">{tenant.tenant_code}</span>
        </div>
      </div>

      {/* Warning */}
      {tempPw && (
        <div
          className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3"
          data-testid="cft-password-warning"
        >
          <AlertCircle size={18} className="text-amber-300 shrink-0 mt-0.5" />
          <div className="text-amber-100 text-sm">
            <strong className="text-amber-200">Password ini hanya ditampilkan sekali.</strong>{" "}
            Simpan sekarang di tempat aman (password manager). Setelah panel ini ditutup, kamu tidak bisa melihat password lagi — kalau lupa, gunakan tombol
            <em> Reset Password </em> di halaman Tenant Detail.
          </div>
        </div>
      )}

      {/* Subscription summary (P1-A) */}
      {result.subscription && (
        <div
          className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4"
          data-testid="cft-subscription-card"
        >
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">Subscription Aktif</div>
          <div className="grid gap-1.5 md:grid-cols-2 text-sm text-white/80">
            <div>Plan: <strong className="text-cyan-300" data-testid="cft-sub-plan">{result.subscription.plan_code}</strong></div>
            <div>Durasi: <strong data-testid="cft-sub-type">{result.subscription.subscription_type}</strong></div>
            <div>Status: <strong className="text-emerald-300" data-testid="cft-sub-status">{result.subscription.status}</strong></div>
            <div data-testid="cft-sub-expiry">
              Berakhir:{" "}
              {result.subscription.subscription_type === "LIFETIME" || !result.subscription.end_date ? (
                <strong className="text-amber-300">Lifetime — tidak ada masa berakhir</strong>
              ) : (
                <strong>{new Date(result.subscription.end_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}</strong>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Credentials Card */}
      <div
        className="rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 via-purple-500/5 to-emerald-500/5 p-5"
        data-testid="cft-admin-credentials-card"
      >
        <div className="flex items-center gap-2 mb-4">
          <Shield size={18} className="text-cyan-300" />
          <div className="text-white font-display font-black text-base">Kredensial Tenant Admin</div>
        </div>

        {/* Email */}
        <div className="mb-3" data-testid="cft-admin-email-row">
          <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Admin Email</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 font-mono text-sm text-white break-all" data-testid="cft-admin-email-value">
              {admin?.email || "—"}
            </div>
            {admin?.email && (
              <Button
                onClick={() => copy(admin.email, "email")}
                variant="outline"
                className="border-white/10 bg-white/5 text-white hover:bg-white/10 shrink-0"
                data-testid="cft-admin-email-copy"
              >
                {copied === "email" ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            )}
          </div>
        </div>

        {/* Temporary Password */}
        {tempPw ? (
          <div className="mb-3" data-testid="cft-admin-password-row">
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <KeyRound size={11} /> Temporary Password
            </label>
            <div className="flex items-center gap-2">
              <div
                className="flex-1 rounded-lg bg-black/40 border border-amber-500/30 px-3 py-2.5 font-mono text-sm text-amber-100 break-all select-all"
                data-testid="cft-admin-password-value"
              >
                {showPw ? tempPw : "•".repeat(Math.min(20, tempPw.length))}
              </div>
              <Button
                onClick={() => setShowPw((v) => !v)}
                variant="outline"
                className="border-white/10 bg-white/5 text-white hover:bg-white/10 shrink-0"
                data-testid="cft-admin-password-toggle"
                aria-label={showPw ? "Sembunyikan password" : "Tampilkan password"}
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </Button>
              <Button
                onClick={() => copy(tempPw, "password")}
                variant="outline"
                className="border-cyan-500/40 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/20 shrink-0"
                data-testid="cft-admin-password-copy"
              >
                {copied === "password" ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            </div>
            <div className="text-[10px] text-white/40 mt-1">
              Setelah admin login pertama kali, sistem akan meminta untuk mengganti password.
            </div>
          </div>
        ) : (
          <div className="text-white/50 text-sm bg-black/20 rounded-lg p-3 border border-white/10" data-testid="cft-admin-password-preexisting">
            User admin dengan email <strong className="text-white/80">{admin?.email}</strong> sudah ada sebelumnya — password lama tetap dipakai. Gunakan tombol <em>Reset Password</em> di Tenant Detail bila diperlukan.
          </div>
        )}

        {/* Copy Both Button */}
        {tempPw && (
          <Button
            onClick={() => copy(`Email: ${admin.email}\nPassword: ${tempPw}`, "both")}
            className="w-full mt-2 bg-white/10 hover:bg-white/15 text-white border border-white/10"
            data-testid="cft-admin-copy-both"
          >
            {copied === "both" ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
            Salin Email + Password
          </Button>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 justify-center pt-2">
        <Button
          onClick={onOpenTenantDetail}
          className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white font-black"
          data-testid="cft-open-tenant-detail-btn"
        >
          Buka Tenant Detail <ArrowRight size={14} className="ml-1" />
        </Button>
        {adminUrl && (
          <Button
            onClick={() => window.open(adminUrl, "_blank", "noopener,noreferrer")}
            variant="outline"
            className="border-emerald-500/40 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20"
            data-testid="cft-open-admin-login-btn"
          >
            Buka Admin Login <ExternalLink size={13} className="ml-1" />
          </Button>
        )}
      </div>
      <div className="text-center text-[11px] text-white/40 pt-1">
        Tenant status: <span className="text-amber-300 font-black">DRAFT</span> — untuk go-live, buka Tenant Detail dan klik <em>Launch Website</em>.
      </div>
    </div>
  );
}

// ============== Main Component ==============
export default function CreateTenantFromTemplateWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [slugCheck, setSlugCheck] = useState({ checking: false, available: null, error: null });
  const [submitting, setSubmitting] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const slugTimer = useRef(null);

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Load available templates on mount
  useEffect(() => {
    (async () => {
      setLoadingTemplates(true);
      try {
        const r = await api.get("/platform/template-registry", { params: { status: "PUBLISHED", limit: 100 } });
        const items = (r.data.items || []).filter((t) => ["PUBLISHED", "ACTIVE"].includes(t.status));
        setTemplates(items);
      } catch (e) {
        notifyError(e, "Gagal memuat daftar template");
      } finally {
        setLoadingTemplates(false);
      }
    })();
  }, []);

  // Auto-slug from name
  useEffect(() => {
    if (!form.tenant_slug_edited) {
      setForm((f) => ({ ...f, tenant_slug: slugify(f.tenant_name) }));
    }
  }, [form.tenant_name, form.tenant_slug_edited]);

  // Debounced slug availability check
  useEffect(() => {
    if (slugTimer.current) clearTimeout(slugTimer.current);
    const slug = form.tenant_slug;
    if (!slug) {
      setSlugCheck({ checking: false, available: null, error: null });
      return;
    }
    const localErr = validateSlug(slug);
    if (localErr) {
      setSlugCheck({ checking: false, available: false, error: localErr });
      return;
    }
    setSlugCheck({ checking: true, available: null, error: null });
    slugTimer.current = setTimeout(async () => {
      try {
        const r = await api.get("/platform/tenants/check-slug", { params: { slug } });
        setSlugCheck({ checking: false, available: r.data.available, error: r.data.error });
      } catch {
        setSlugCheck({ checking: false, available: null, error: "Gagal cek slug" });
      }
    }, 400);
    return () => slugTimer.current && clearTimeout(slugTimer.current);
  }, [form.tenant_slug]);

  // Prefill timezone/currency from selected template's defaults
  const handleSelectTemplate = useCallback((tpl) => {
    setSelectedTpl(tpl);
    const biz = tpl.business_profile || {};
    setForm((f) => ({
      ...f,
      template_id: tpl.id,
      timezone: biz.timezone || tpl.default_configuration?.timezone || f.timezone,
      currency: (biz.currency || tpl.default_configuration?.currency || f.currency || "IDR").toUpperCase(),
    }));
  }, []);

  const emailValid = form.owner_email && /^\S+@\S+\.\S+$/.test(form.owner_email);

  const stepValid = useMemo(() => {
    if (step === 1) return !!form.template_id && !!selectedTpl;
    if (step === 2) {
      return (
        form.tenant_name.trim().length > 0 &&
        !validateSlug(form.tenant_slug) &&
        slugCheck.available === true &&
        form.owner_name.trim().length > 0 &&
        emailValid
      );
    }
    if (step === 3) {
      return !!form.package && !!form.subscription_type;
    }
    return true;
  }, [step, form, selectedTpl, slugCheck.available, emailValid]);

  const goNext = () => stepValid && setStep((s) => Math.min(STEPS.length, s + 1));
  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setStep(5);
    try {
      const payload = {
        template_id: form.template_id,
        tenant_name: form.tenant_name.trim(),
        tenant_slug: form.tenant_slug.trim(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
        owner_phone: form.owner_phone.trim(),
        timezone: form.timezone,
        currency: form.currency,
        package: form.package,
        subscription_type: form.subscription_type,
        notes: form.notes.trim(),
      };
      const r = await api.post("/platform/tenants/from-template", payload);
      setCreatedResult(r.data);
      toast.success(`Tenant "${r.data.tenant.tenant_name}" berhasil dibuat (${r.data.tenant.tenant_code})`);
      // HOTFIX Tenant Admin Bootstrap: BUKAN auto-redirect — kredensial admin
      // harus ditampilkan sekali dan disimpan operator sebelum lanjut.
    } catch (e) {
      notifyError(e, "Gagal membuat tenant");
      setStep(4); // Back to review so user can retry
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PlatformLayout title="Create Tenant From Template">
      <div data-testid="cft-page" className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate("/platform/tenants")}
          className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white mb-4"
          data-testid="cft-back-link"
        >
          <ArrowLeft size={12} /> Kembali ke Tenant List
        </button>

        <div className="mb-6">
          <h1 className="text-white font-display font-black text-3xl">Create Tenant From Template</h1>
          <p className="text-white/50 text-sm mt-1">
            Pilih template dari registry dan buat tenant baru. Tenant akan disimpan sebagai
            <span className="mx-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] font-bold uppercase">DRAFT</span>
            dan belum di-launch ke infrastruktur.
          </p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-[#0F172A] p-6 shadow-2xl">
          <StepIndicator current={step} />

          {/* ============= STEP 1 ============= */}
          {step === 1 && (
            <div data-testid="cft-step-1">
              <div className="mb-4">
                <h2 className="text-white font-display font-black text-lg flex items-center gap-2">
                  <Layers size={18} className="text-cyan-400" /> Pilih Template
                </h2>
                <p className="text-white/50 text-xs mt-1">
                  Template menentukan branding, feature flags, provider defaults, dan konfigurasi awal tenant.
                </p>
              </div>

              {loadingTemplates ? (
                <div className="flex items-center gap-2 text-white/60 text-sm" data-testid="cft-templates-loading">
                  <Loader2 size={16} className="animate-spin" /> Memuat template…
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-white/50" data-testid="cft-templates-empty">
                  Tidak ada template PUBLISHED yang tersedia.
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2" data-testid="cft-template-grid">
                  {templates.map((tpl) => (
                    <TemplateCard
                      key={tpl.id}
                      tpl={tpl}
                      selected={form.template_id === tpl.id}
                      onSelect={handleSelectTemplate}
                    />
                  ))}
                </div>
              )}

              {selectedTpl && (
                <div
                  className="mt-6 rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-4"
                  data-testid="cft-selected-summary"
                >
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">Template Dipilih</div>
                  <div className="grid gap-2 md:grid-cols-3">
                    <div className="flex items-center gap-2">
                      <Palette size={14} className="text-white/50" />
                      <span className="text-xs text-white/80 truncate">Theme: <span className="font-mono">{selectedTpl.branding?.theme_color || "—"}</span></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Server size={14} className="text-white/50" />
                      <span className="text-xs text-white/80 truncate">
                        {selectedTpl.provider_defaults?.payment || "—"} · {selectedTpl.provider_defaults?.shipping || "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Settings2 size={14} className="text-white/50" />
                      <span className="text-xs text-white/80 truncate">
                        {selectedTpl.default_configuration?.currency || "—"} · {selectedTpl.default_configuration?.timezone || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============= STEP 2 ============= */}
          {step === 2 && (
            <div data-testid="cft-step-2" className="grid gap-4 md:grid-cols-2">
              <Field label="Nama Tenant" required testId="cft-field-name">
                <Input
                  autoFocus
                  value={form.tenant_name}
                  onChange={(e) => setField({ tenant_name: e.target.value })}
                  placeholder="Contoh: Toko Kelontong Bahagia"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="cft-input-name"
                />
              </Field>

              <Field
                label="Slug"
                required
                error={slugCheck.error}
                hint="Auto dari nama. Bisa diubah manual — lowercase, angka, dan dash."
                testId="cft-field-slug"
              >
                <div className="relative">
                  <Input
                    value={form.tenant_slug}
                    onChange={(e) =>
                      setField({
                        tenant_slug: e.target.value.toLowerCase(),
                        tenant_slug_edited: true,
                      })
                    }
                    placeholder="toko-bahagia"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pr-9 font-mono"
                    data-testid="cft-input-slug"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    {slugCheck.checking ? (
                      <Loader2 size={14} className="text-white/40 animate-spin" />
                    ) : slugCheck.available === true ? (
                      <Check size={14} className="text-emerald-400" data-testid="cft-slug-ok" />
                    ) : slugCheck.available === false ? (
                      <AlertCircle size={14} className="text-rose-400" />
                    ) : null}
                  </div>
                </div>
              </Field>

              <Field label="Nama Owner" required testId="cft-field-owner-name">
                <Input
                  value={form.owner_name}
                  onChange={(e) => setField({ owner_name: e.target.value })}
                  placeholder="Nama lengkap owner tenant"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="cft-input-owner-name"
                />
              </Field>

              <Field
                label="Email Owner"
                required
                error={form.owner_email && !emailValid ? "Format email tidak valid." : null}
                testId="cft-field-owner-email"
              >
                <Input
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => setField({ owner_email: e.target.value })}
                  placeholder="owner@tenant.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="cft-input-owner-email"
                />
              </Field>

              <Field label="Nomor WhatsApp / HP" testId="cft-field-owner-phone">
                <Input
                  value={form.owner_phone}
                  onChange={(e) => setField({ owner_phone: e.target.value })}
                  placeholder="081234567890"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="cft-input-owner-phone"
                />
              </Field>

              <Field label="Timezone" testId="cft-field-timezone">
                <select
                  value={form.timezone}
                  onChange={(e) => setField({ timezone: e.target.value })}
                  className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
                  data-testid="cft-select-timezone"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz} className="bg-[#0F172A]">
                      {tz}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Currency" testId="cft-field-currency">
                <select
                  value={form.currency}
                  onChange={(e) => setField({ currency: e.target.value })}
                  className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
                  data-testid="cft-select-currency"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c} className="bg-[#0F172A]">
                      {c}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="md:col-span-2">
                <Field label="Catatan (Internal)" testId="cft-field-notes">
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setField({ notes: e.target.value })}
                    placeholder="Catatan internal Platform Owner untuk tenant ini."
                    rows={3}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="cft-input-notes"
                  />
                </Field>
              </div>
            </div>
          )}

          {/* ============= STEP 3 — Paket & Langganan (P1-A) ============= */}
          {step === 3 && (
            <div data-testid="cft-step-plan">
              <div className="mb-4">
                <h2 className="text-white font-display font-black text-lg flex items-center gap-2">
                  <Package size={18} className="text-cyan-400" /> Paket & Langganan
                </h2>
                <p className="text-white/50 text-xs mt-1">
                  Subscription dibuat otomatis saat tenant dibuat — status ACTIVE, tanggal berakhir dihitung sistem.
                </p>
              </div>

              <div className="text-[11px] font-bold text-white/60 uppercase tracking-wider mb-2">Package</div>
              <div className="grid gap-3 md:grid-cols-3 mb-6" data-testid="cft-package-grid">
                {PACKAGES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setField({ package: p.value })}
                    className={`text-left rounded-2xl border p-4 transition-all ${
                      form.package === p.value
                        ? "border-cyan-400 bg-cyan-500/10 ring-2 ring-cyan-400/40"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                    data-testid={`cft-package-${p.value.toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white font-display font-black text-sm">{p.label}</span>
                      {form.package === p.value && <Check size={16} className="text-cyan-400" />}
                    </div>
                    <p className="text-white/50 text-[11px] mt-1">{p.desc}</p>
                  </button>
                ))}
              </div>

              <div className="text-[11px] font-bold text-white/60 uppercase tracking-wider mb-2">Durasi Langganan</div>
              <div className="grid gap-3 md:grid-cols-4 mb-4" data-testid="cft-duration-grid">
                {DURATIONS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setField({ subscription_type: d.value })}
                    className={`text-left rounded-2xl border p-4 transition-all ${
                      form.subscription_type === d.value
                        ? "border-emerald-400 bg-emerald-500/10 ring-2 ring-emerald-400/40"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                    data-testid={`cft-duration-${d.value.toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white font-display font-black text-sm">{d.label}</span>
                      {form.subscription_type === d.value && <Check size={16} className="text-emerald-400" />}
                    </div>
                    <p className="text-white/50 text-[11px] mt-1">{d.desc}</p>
                  </button>
                ))}
              </div>

              <div
                className={`rounded-xl border px-4 py-3 text-xs ${
                  form.subscription_type === "LIFETIME"
                    ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                    : "border-white/10 bg-white/5 text-white/60"
                }`}
                data-testid="cft-plan-summary"
              >
                <strong className="text-white/90">{form.package}</strong> · {durationLabel(form.subscription_type)} — {expiryHint(form.subscription_type)}
              </div>
            </div>
          )}

          {/* ============= STEP 4 — Review ============= */}
          {step === 4 && (
            <div data-testid="cft-step-3">
              <div className="mb-4">
                <h2 className="text-white font-display font-black text-lg">Review Sebelum Simpan</h2>
                <p className="text-white/50 text-xs mt-1">
                  Cek data di bawah. Tenant akan disimpan sebagai <strong className="text-amber-300">DRAFT</strong> —
                  belum di-launch ke infrastruktur.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 divide-y divide-white/5 bg-white/5">
                <ReviewRow
                  label="Template"
                  value={
                    selectedTpl ? (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-md shrink-0"
                          style={{
                            background: `linear-gradient(135deg, ${selectedTpl.branding?.theme_color}, ${selectedTpl.branding?.accent_color})`,
                          }}
                        />
                        <span className="font-bold">{selectedTpl.name}</span>
                        <span className="text-white/40 font-mono text-xs">({selectedTpl.slug})</span>
                      </div>
                    ) : (
                      "—"
                    )
                  }
                  testId="cft-review-template"
                />
                <ReviewRow label="Nama Tenant" value={form.tenant_name} testId="cft-review-name" />
                <ReviewRow label="Slug" value={<span className="font-mono">{form.tenant_slug}</span>} testId="cft-review-slug" />
                <ReviewRow label="Owner" value={form.owner_name} testId="cft-review-owner-name" />
                <ReviewRow label="Email" value={form.owner_email} testId="cft-review-owner-email" />
                <ReviewRow label="Nomor HP" value={form.owner_phone} testId="cft-review-owner-phone" />
                <ReviewRow label="Timezone" value={form.timezone} testId="cft-review-timezone" />
                <ReviewRow label="Currency" value={form.currency} testId="cft-review-currency" />
                <ReviewRow
                  label="Package"
                  value={<span className="font-bold text-cyan-300">{form.package}</span>}
                  testId="cft-review-package"
                />
                <ReviewRow
                  label="Durasi Langganan"
                  value={<span className="font-bold text-emerald-300">{durationLabel(form.subscription_type)}</span>}
                  testId="cft-review-duration"
                />
                <ReviewRow label="Mulai Langganan" value="Saat tenant dibuat (hari ini)" testId="cft-review-start" />
                <ReviewRow
                  label="Berakhir"
                  value={
                    form.subscription_type === "LIFETIME" ? (
                      <span className="font-bold text-amber-300">Lifetime — tidak ada masa berakhir</span>
                    ) : (
                      expiryHint(form.subscription_type)
                    )
                  }
                  testId="cft-review-expiry"
                />
                {form.notes && <ReviewRow label="Catatan" value={form.notes} testId="cft-review-notes" />}
                <ReviewRow
                  label="Status Awal"
                  value={
                    <span className="inline-block px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] font-bold uppercase">
                      DRAFT
                    </span>
                  }
                  testId="cft-review-status"
                />
              </div>

              <div className="mt-4 text-[11px] text-white/40 bg-white/5 rounded-lg p-3 border border-white/10">
                <strong className="text-white/70">tenant_id (UUID)</strong> dan{" "}
                <strong className="text-white/70">tenant_code (TEN-XXXXXX)</strong> akan di-generate
                otomatis. Konfigurasi awal (email/payment/shipping provider) di-copy dari template dan
                <strong className="text-white/70"> credential tidak dibawa</strong> — akan diatur nanti di
                Tenant Configuration.
              </div>
            </div>
          )}

          {/* ============= STEP 5 — Selesai ============= */}
          {step === 5 && (
            <div data-testid="cft-step-4" className="py-4">
              {submitting ? (
                <div className="flex flex-col items-center gap-3 text-center py-8">
                  <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
                  <div className="text-white font-display font-black text-lg">Membuat tenant…</div>
                  <div className="text-white/50 text-sm">Menyalin metadata & konfigurasi dari template + membuat akun admin.</div>
                </div>
              ) : createdResult ? (
                <SuccessPanel
                  result={createdResult}
                  onOpenTenantDetail={() =>
                    navigate(`/platform/tenants/${createdResult.tenant.tenant_id}?created=1`)
                  }
                />
              ) : null}
            </div>
          )}

          {/* ============= FOOTER ============= */}
          {step < 5 && (
            <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate("/platform/tenants")}
                className="text-white/60 hover:text-white hover:bg-white/10"
                data-testid="cft-cancel-btn"
              >
                Batal
              </Button>
              <div className="ml-auto flex items-center gap-2">
                {step > 1 && (
                  <Button
                    variant="outline"
                    onClick={goPrev}
                    className="border-white/10 bg-transparent text-white hover:bg-white/10"
                    data-testid="cft-prev-btn"
                  >
                    <ArrowLeft size={14} className="mr-1" /> Kembali
                  </Button>
                )}
                {step < 4 ? (
                  <Button
                    onClick={goNext}
                    disabled={!stepValid}
                    className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white hover:opacity-90 disabled:opacity-40"
                    data-testid="cft-next-btn"
                  >
                    Lanjut <ArrowRight size={14} className="ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={submitting}
                    className="bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40"
                    data-testid="cft-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…
                      </>
                    ) : (
                      <>
                        <Check size={14} className="mr-1" /> Buat Tenant
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </PlatformLayout>
  );
}
